import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LineItemDto, CreateLineItemInput } from '@cutlyft/zod-schemas';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';

interface CreateLineItemApiResponse {
  success: boolean;
  data: LineItemDto;
}

interface CreateLineItemMutationInput {
  projectId: number;
  input: CreateLineItemInput;
}

/**
 * Mutation hook for adding a new line item to a project.
 * Calls POST /projects/:projectId/line-items.
 * The API runs the calculator immediately and returns all derived columns.
 * Invalidates the project's line-items list on success.
 */
export function useCreateLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, input }: CreateLineItemMutationInput) => {
      const response = await apiClient.post<CreateLineItemApiResponse>(
        `/projects/${projectId}/line-items`,
        input,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      // 3-element prefix so all list queries for this project (any filter/cursor) refetch.
      queryClient.invalidateQueries({ queryKey: ['lineItems', 'list', data.projectId] });
    },
  });
}
